import React from "react";
import { createPortal } from "react-dom";

import { cn } from "../../lib/utils";

export type DropdownOption<T extends string> = {
  value: T;
  label: string;
};

export function Dropdown<T extends string>({
  value,
  options,
  onChange,
  buttonClassName,
}: {
  value: T;
  options: Array<DropdownOption<T>>;
  onChange: (value: T) => void;
  buttonClassName?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const [menuStyle, setMenuStyle] = React.useState<React.CSSProperties>({});

  React.useEffect(() => {
    function onDocPointerDown(e: PointerEvent) {
      const root = rootRef.current;
      if (!root) return;
      if (e.target instanceof Node && root.contains(e.target)) return;
      setOpen(false);
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", onDocPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onDocPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  React.useEffect(() => {
    if (!open) return;

    function clamp(n: number, min: number, max: number) {
      return Math.min(max, Math.max(min, n));
    }

    function update() {
      const btn = buttonRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const width = Math.ceil(r.width);
      const padding = 8;
      const maxLeft = window.innerWidth - padding - width;
      const left = clamp(Math.round(r.left), padding, Math.max(padding, maxLeft));
      const top = Math.round(r.bottom - 1);

      setMenuStyle({
        position: "fixed",
        top,
        left,
        width,
        zIndex: 99999,
      });
    }

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  const current = options.find((o) => o.value === value) ?? options[0];

  return (
    <div ref={rootRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        ref={buttonRef}
        className={cn(
          "inline-flex h-11 w-[220px] appearance-none items-center justify-between gap-2 rounded-[10px] border border-black/20 bg-white px-4 font-[var(--font-sans)] text-[13px] font-semibold text-black",
          open && "rounded-b-none",
          "hover:border-black/30 focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/10",
          buttonClassName
        )}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="text-black/60">Sort</span>
          <span className="truncate text-black">{current?.label ?? ""}</span>
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="h-4 w-px bg-black/10" />
          <svg
            className={cn("h-4 w-4 text-black/60 transition", open && "rotate-180")}
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M5.5 7.5a1 1 0 0 1 1.4 0L10 10.6l3.1-3.1a1 1 0 1 1 1.4 1.4l-3.8 3.8a1 1 0 0 1-1.4 0L5.5 8.9a1 1 0 0 1 0-1.4Z"
              fill="currentColor"
            />
          </svg>
        </span>
      </button>

      {typeof document !== "undefined"
        ? createPortal(
            <div
              style={menuStyle}
              className={cn(
                "rounded-[10px] border border-black/20 bg-white p-0 text-black shadow-[0_10px_26px_rgba(0,0,0,0.10)]",
                open && "rounded-t-none border-t-0",
                "transition duration-150",
                open ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-1 opacity-0"
              )}
              role="menu"
            >
              {options.map((opt, idx) => {
                const active = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={active}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={cn(
                      "flex w-full appearance-none items-center justify-between border-0 bg-white px-4 py-2 text-left font-[var(--font-sans)] text-[13px] font-semibold text-black transition",
                      idx > 0 && "border-t border-black/10",
                      idx === 0 && !open && "rounded-t-[10px]",
                      idx === options.length - 1 && "rounded-b-[10px]",
                      "focus:outline-none focus-visible:outline-none",
                      active ? "" : "hover:bg-black/5 focus-visible:bg-black/5"
                    )}
                  >
                    <span>{opt.label}</span>
                    {active ? <span className="h-1.5 w-1.5 rounded-full bg-black/40" /> : null}
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
